import styled from 'styled-components'
import { TdsTooltip } from '@scania/tegel-react'
import { useAppSelector } from '../../../store/hooks'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { getMarketDenomination } from '../../../store/sessionDataSlice'
import {
  getLatestConfigChangeResult,
  getTruckInfoState,
} from '../../../store/truckInfoSlice'
import { TextId, useTextsV2 } from '../../../utils/useTexts'
import {
  FAMILY_ID_APPLICATION,
  OPERATION_FAMILY_ID,
} from '../../../utils/constants'
import { useResizeObserver } from '../../../utils/useResizeObserver'
import { Size2d } from '../../../types/Size2d'

const MiniSummaryRoot = styled.section``

const MiniSummaryRowWrapper = styled.div`
  // IMPORTANT:
  // Without this line iPhone in landscape mode may use different font sizes for
  // the text and the Dots .... elements!!
  text-size-adjust: 100%;

  margin-block-end: 1em;
  margin-block-start: 1em;
  width: 100%;
  display: flex;
  align-items: center;
`
const MiniSummaryRowTitle = styled.div`
  color: var(--tds-grey-700);
  white-space: nowrap;
`

const Dots = styled.div`
  color: var(--tds-grey-700);
  text-align: center;
`

const MiniSummaryRowText = styled.div`
  color: var(--tds-grey-958);
  white-space: nowrap;
`

interface SummaryItemAdjusted {
  itemId: string
  itemText: string
  itemTextAdjusted: string
  menuId: string
  menuText: string
  menuTextAdjusted: string
  menuPath?: string | null
  dots: string
}

export interface MinisummaryProps {
  showPackages: boolean
}

export function MiniSummary({ showPackages }: MinisummaryProps): JSX.Element {
  const miniSummaryState = useAppSelector(
    getLatestConfigChangeResult,
  )?.miniSummary
  const marketDenomination = useAppSelector(getMarketDenomination)
  const truckInfo = useAppSelector(getTruckInfoState)
  const t2 = useTextsV2()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const size = useResizeObserver<Size2d>(rootRef, { width: 0, height: 0 })
  const [miniSummary, setMiniSummary] = useState<SummaryItemAdjusted[]>([])
  const packagesState = useAppSelector(getLatestConfigChangeResult)?.packages
    .items
  const [packagesData, setPackagesData] = useState<
    SummaryItemAdjusted[] | null
  >(null)
  const rowRefs = useRef<(HTMLDivElement | null)[]>([])
  const rowRefsPackages = useRef<(HTMLDivElement | null)[]>([])

  // Find if any Packages should be presented
  useEffect(() => {
    if (!showPackages) {
      return
    }
    if (!packagesState) {
      return
    }
    let packages: SummaryItemAdjusted[] = []
    packagesState.forEach((item) => {
      if (item.itemText !== 'without') {
        const packageItem: SummaryItemAdjusted = {
          itemId: item.itemId,
          itemText: item.itemText,
          itemTextAdjusted: item.itemText,
          menuId: item.menuId,
          menuText: item.menuText,
          menuTextAdjusted: item.menuText,
          menuPath: item.menuPath,
          dots: '...',
        }
        packages.push(packageItem)
      }
    })
    setPackagesData(packages)
  }, [packagesState, showPackages])

  // Get colorItems and nonColorItems from miniSummaryState
  useEffect(() => {
    const newMiniSummaryState: SummaryItemAdjusted[] = []

    miniSummaryState?.items?.forEach((element) => {
      if (
        element.itemId.startsWith(OPERATION_FAMILY_ID) &&
        truckInfo?.isFactoryModel
      ) {
        return
      }
      if (element.itemId.startsWith(FAMILY_ID_APPLICATION)) {
        return
      } else {
        const summaryItem: SummaryItemAdjusted = {
          itemId: element.itemId,
          itemText: element.itemText,
          itemTextAdjusted: element.itemText,
          menuId: element.menuId,
          menuText: element.menuText,
          menuTextAdjusted: element.menuText,
          menuPath: element.menuPath,
          dots: '...',
        }
        newMiniSummaryState.push(summaryItem)
      }
    })
    if (miniSummaryState?.cabColor) {
      const cabColor = {
        ...miniSummaryState.cabColor,
        menuText: t2(TextId.LABEL_COLOR_CAB),
        menuTextAdjusted: t2(TextId.LABEL_COLOR_CAB),
        itemTextAdjusted: miniSummaryState.cabColor.itemText,
        dots: '...',
      }
      newMiniSummaryState.push(cabColor)
    }
    setMiniSummary(newMiniSummaryState)
  }, [marketDenomination, miniSummaryState, truckInfo, t2])

  const adjustRow = (
    titleElem: HTMLDivElement | null,
    textElem: HTMLDivElement | null,
    dotsElem: HTMLDivElement | null,
    item: SummaryItemAdjusted,
  ) => {
    if (!titleElem || !textElem || !dotsElem) {
      return
    }
    const availableWidth = titleElem.parentElement?.offsetWidth || 0

    // Reset the adjusted strings
    dotsElem.textContent = '...'
    titleElem.textContent = item.menuText
    textElem.textContent = item.itemText
    var addedDotsText = false

    // Adjust content by checking available width
    while (
      titleElem.offsetWidth + textElem.offsetWidth + dotsElem.offsetWidth >
      availableWidth
    ) {
      if (titleElem.textContent && titleElem.textContent.length > 6) {
        titleElem.textContent = titleElem.textContent.slice(0, -1)
      }

      if (textElem.textContent && textElem.textContent.length > 6) {
        if (!addedDotsText) {
          textElem.textContent = textElem.textContent.slice(0, -1)
          textElem.textContent += '...'
          addedDotsText = true
        } else {
          textElem.textContent =
            textElem.textContent.slice(0, -4) + textElem.textContent.slice(-3)
        }
      }

      if (
        titleElem.textContent &&
        titleElem.textContent.length <= 6 &&
        textElem.textContent &&
        textElem.textContent.length <= 6
      ) {
        break
      }
    }

    // Fill any available space with more dots
    while (
      titleElem.offsetWidth + textElem.offsetWidth + dotsElem.offsetWidth <
      availableWidth
    ) {
      dotsElem.textContent += '.'
    }
  }

  useLayoutEffect(() => {
    miniSummary.forEach((item, index) => {
      const titleElem = rowRefs.current[index]?.querySelector(
        '.title',
      ) as HTMLDivElement
      const textElem = rowRefs.current[index]?.querySelector(
        '.text',
      ) as HTMLDivElement
      const dotsElem = rowRefs.current[index]?.querySelector(
        '.dots',
      ) as HTMLDivElement

      adjustRow(titleElem, textElem, dotsElem, item)
    })

    if (packagesData) {
      packagesData.forEach((item, index) => {
        const titleElem = rowRefsPackages.current[index]?.querySelector(
          '.titlePackages',
        ) as HTMLDivElement
        const textElem = rowRefsPackages.current[index]?.querySelector(
          '.textPackages',
        ) as HTMLDivElement
        const dotsElem = rowRefsPackages.current[index]?.querySelector(
          '.dotsPackages',
        ) as HTMLDivElement
        adjustRow(titleElem, textElem, dotsElem, item)
      })
    }
  }, [size, miniSummary, packagesData])

  return (
    <MiniSummaryRoot ref={rootRef}>
      {miniSummary.map((item, index) => (
        <MiniSummaryRowWrapper
          key={'minisummarynoncolor' + item.itemId + '-' + index}
          ref={(el) => (rowRefs.current[index] = el)}
        >
          <MiniSummaryRowTitle id={'NoncolorTitle' + index} className="title">
            {item.menuTextAdjusted}
          </MiniSummaryRowTitle>
          <TdsTooltip
            placement="bottom"
            selector={'#NoncolorTitle' + index}
            text={item.menuText}
          />
          <Dots className="dots">{item.dots}</Dots>
          <MiniSummaryRowText id={'Noncolor' + index} className="text">
            <span
              data-test-go-group-id={item.itemId.split('~')[0]}
              data-etel-variant-id={item.itemId}
            >
              {item.itemTextAdjusted}
            </span>
          </MiniSummaryRowText>
          <TdsTooltip
            placement="bottom"
            selector={'#Noncolor' + index}
            text={item.itemText}
          />
        </MiniSummaryRowWrapper>
      ))}
      {packagesData && (
        <div style={{ marginTop: '32px' }}>
          <h4>{t2(TextId.HEADER_GV_PACKAGES)}</h4>
          {packagesData.map((item, index) => (
            <MiniSummaryRowWrapper
              key={'packages' + item.itemId + '-' + index}
              ref={(el) => (rowRefsPackages.current[index] = el)}
            >
              <MiniSummaryRowTitle
                id={'packagesTitle' + index}
                className="titlePackages"
              >
                {item.menuTextAdjusted}
              </MiniSummaryRowTitle>
              <TdsTooltip
                placement="bottom"
                selector={'#packagesTitle' + index}
                text={item.menuText}
              />
              <Dots className="dotsPackages">{item.dots}</Dots>
              <MiniSummaryRowText
                id={'packages' + index}
                className="textPackages"
              >
                <span
                  data-test-go-group-id={item.itemId.split('~')[0]}
                  data-etel-variant-id={item.itemId}
                >
                  {item.itemTextAdjusted}
                </span>
              </MiniSummaryRowText>
              <TdsTooltip
                placement="bottom"
                selector={'#packages' + index}
                text={item.itemText}
              />
            </MiniSummaryRowWrapper>
          ))}
        </div>
      )}
    </MiniSummaryRoot>
  )
}
